import { useState, useEffect, type FormEvent, type ChangeEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { showToast } from '@/components/layout/Toast'
import { supabase } from '@/lib/supabase'
import type { Factura, FacturaItem, FacturaAdjunto, Cliente, ContactoVinculado, Servicio, Proyecto, Moneda, ConfigDetraccion, TipoDocumentoSunat } from '@/lib/types'
import { DEFAULT_DETRACCION, contactoFacturacionDe } from '@/lib/types'
import { r2, formatCurrency, nextFacturaNumber, calcularDetraccion } from '@/lib/utils'
import { PageHeader } from '@/components/ui'

const SUNAT_API_ENABLED = import.meta.env.VITE_SUNAT_API_ENABLED === 'true'
const emptyItem: FacturaItem = { tipo: 'fijo', descripcion: '', precio_unitario: 0, cantidad: 1, total: 0 }

export default function NuevaFactura() {
  const db = useSupabase()
  const nav = useNavigate()
  const { id } = useParams()
  const isEdit = !!id

  // ─── State ───
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [servicios, setServicios] = useState<Servicio[]>([])
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)

  const [numero, setNumero] = useState('')
  const [nombreInterno, setNombreInterno] = useState('')
  const [clienteId, setClienteId] = useState<number | null>(null)
  const [clienteNombre, setClienteNombre] = useState('')
  const [clienteContacto, setClienteContacto] = useState('')
  const [clienteRuc, setClienteRuc] = useState('')
  const [clienteEmail, setClienteEmail] = useState('')
  // Contacto elegido para la factura (fuente de verdad = relación, ya no columna plana)
  const [clienteContactos, setClienteContactos] = useState<ContactoVinculado[]>([])
  const [contactoSelId, setContactoSelId] = useState<number | null>(null)
  const [proyectoNombre, setProyectoNombre] = useState('')
  const [proyectoId, setProyectoId] = useState<number | null>(null)
  const [proyectosCliente, setProyectosCliente] = useState<Proyecto[]>([])
  const [fechaEmision, setFechaEmision] = useState(new Date().toISOString().slice(0, 10))
  const [fechaVencimiento, setFechaVencimiento] = useState('')
  const [moneda, setMoneda] = useState<string>('USD')
  const [igvPorcentaje, setIgvPorcentaje] = useState(18)
  const [igvMode, setIgvMode] = useState<'agregar' | 'incluido'>('agregar')
  const [notas, setNotas] = useState('')
  const [items, setItems] = useState<FacturaItem[]>([{ ...emptyItem }])
  const [adjuntos, setAdjuntos] = useState<FacturaAdjunto[]>([])
  const [facturaIdForStorage, setFacturaIdForStorage] = useState<number | null>(null)
  const [tipoCambio, setTipoCambio] = useState<number | null>(null)
  const [tcAutoLoaded, setTcAutoLoaded] = useState(false)
  const [tipoDocSunat, setTipoDocSunat] = useState<TipoDocumentoSunat | ''>('')

  // ─── Config SPOT (from DB) ───
  const [configSpot, setConfigSpot] = useState<Omit<ConfigDetraccion, 'id' | 'created_at'>>(DEFAULT_DETRACCION)

  // ─── Calculated values ───
  const rawSubtotal = r2(items.reduce((s, i) => s + i.total, 0))
  const subtotal = igvMode === 'incluido'
    ? r2(rawSubtotal / (1 + igvPorcentaje / 100))
    : rawSubtotal
  const igvMonto = igvMode === 'incluido'
    ? r2(rawSubtotal - subtotal)
    : r2(rawSubtotal * igvPorcentaje / 100)
  const total = igvMode === 'incluido'
    ? rawSubtotal
    : r2(rawSubtotal + igvMonto)
  const detraccion = calcularDetraccion(total, moneda, tipoCambio, configSpot.umbral_pen, configSpot.porcentaje)

  // ─── Load data ───
  useEffect(() => {
    async function init() {
      try {
        const [cls, svcs, allFacturas, configs] = await Promise.all([
          db.getAll<Cliente>('clientes'),
          db.getAll<Servicio>('servicios'),
          db.getAll<Factura>('facturas'),
          db.getAll<ConfigDetraccion>('config_detraccion'),
        ])
        setClientes(cls)
        setServicios(svcs)

        // Load SPOT config from DB (first active record)
        const activeConfig = configs.find(c => c.activo)
        if (activeConfig) {
          setConfigSpot(activeConfig)
        }

        if (isEdit) {
          const factura = await db.getById<Factura>('facturas', Number(id))
          if (factura) {
            setNumero(factura.numero)
            setNombreInterno(factura.nombre_interno ?? '')
            setClienteId(factura.cliente_id)
            setClienteNombre(factura.cliente_nombre ?? '')
            setClienteContacto(factura.cliente_contacto ?? '')
            setClienteRuc(factura.cliente_ruc ?? '')
            setClienteEmail(factura.cliente_email ?? '')
            setProyectoNombre(factura.proyecto_nombre ?? '')
            setProyectoId(factura.proyecto_id ?? null)  // DAT-03: no perder la FK al editar
            setFechaEmision(factura.fecha_emision ?? '')
            setFechaVencimiento(factura.fecha_vencimiento ?? '')
            setMoneda(factura.moneda)
            setIgvPorcentaje(factura.igv_porcentaje)
            setNotas(factura.notas ?? '')
            const itemsArr = Array.isArray(factura.items) ? factura.items : []
            setItems(itemsArr.length > 0 ? itemsArr : [{ ...emptyItem }])
            setAdjuntos(Array.isArray(factura.adjuntos) ? factura.adjuntos : [])
            // LOG-05: el modo de IGV no se persiste; se infiere de los datos guardados.
            // 'incluido' → total ≈ suma de ítems; 'agregar' → total ≈ suma × (1 + igv%).
            // Sin esto, editar una factura "IGV incluido" recalculaba en modo "agregar"
            // e inflaba el total en un 18%.
            const rawSum = r2(itemsArr.reduce((s, i) => s + (i.total || 0), 0))
            const pct = factura.igv_porcentaje ?? 18
            const storedTotal = factura.total ?? 0
            const esIncluido = Math.abs(storedTotal - rawSum) < Math.abs(storedTotal - r2(rawSum * (1 + pct / 100)))
            setIgvMode(esIncluido ? 'incluido' : 'agregar')
            // LOG-05: restaurar el tipo de documento SUNAT (antes se perdía al editar).
            setTipoDocSunat(factura.tipo_documento_sunat ?? '')
            setFacturaIdForStorage(factura.id)
            setTipoCambio(factura.tipo_cambio)
            // Mark auto-loaded only if saved TC is valid (> 1), otherwise let auto-fill correct it
            if (factura.tipo_cambio && factura.tipo_cambio > 1) setTcAutoLoaded(true)
          }
        } else {
          if (SUNAT_API_ENABLED) {
            // Placeholder interno FAC-NNNNNN (NubeFact lo reemplaza con la serie real al emitir)
            setNumero(nextFacturaNumber(allFacturas.map(f => f.numero)))
          } else {
            // Modo manual: sugerir siguiente E001-XXX basado en último registrado
            const e001Numbers = allFacturas
              .map(f => f.numero)
              .filter(n => n && n.startsWith('E001-'))
              .map(n => parseInt(n.replace('E001-', ''), 10))
              .filter(n => !isNaN(n))
            const maxNum = e001Numbers.length > 0 ? Math.max(...e001Numbers) : 0
            setNumero(`E001-${maxNum + 1}`)
          }
        }
      } catch {
        showToast('Error al cargar datos', 'error')
      }
    }
    init()
  }, [])

  // ─── Auto-fill TC from tipo_cambio table ───
  useEffect(() => {
    if (moneda === 'PEN' || tcAutoLoaded) return

    async function loadTC() {
      try {
        // Try exact date first
        const { data: exactMatch } = await supabase
          .from('tipo_cambio')
          .select('venta')
          .eq('fecha', fechaEmision)
          .maybeSingle() as { data: { venta: number } | null }

        if (exactMatch) {
          setTipoCambio(Number(exactMatch.venta))
          setTcAutoLoaded(true)
          return
        }

        // Fallback: last available date before fechaEmision (regla SUNAT)
        const { data: fallback } = await supabase
          .from('tipo_cambio')
          .select('venta, fecha')
          .lte('fecha', fechaEmision)
          .order('fecha', { ascending: false })
          .limit(1)
          .maybeSingle() as { data: { venta: number; fecha: string } | null }

        if (fallback) {
          setTipoCambio(Number(fallback.venta))
          setTcAutoLoaded(true)
        }
      } catch {
        // Silent fail — user can input manually
      }
    }
    loadTC()
  }, [moneda, fechaEmision])

  // ─── Load projects when client changes ───
  useEffect(() => {
    if (!clienteId) { setProyectosCliente([]); return }
    ;(async () => {
      try {
        const { data } = await supabase.from('proyectos').select('*').eq('cliente_id', clienteId).order('nombre')
        setProyectosCliente((data ?? []) as unknown as Proyecto[])
      } catch { /* silent */ }
    })()
  }, [clienteId])

  // ─── Client selection ───
  function elegirContacto(id: number | null) {
    setContactoSelId(id)
    const ct = clienteContactos.find(x => x.id === id)
    setClienteContacto(ct ? [ct.nombre, ct.apellido].filter(Boolean).join(' ') : '')
    setClienteEmail(ct?.email ?? '')
  }

  function selectCliente(cid: number) {
    const c = clientes.find(x => x.id === cid)
    if (c) {
      setClienteId(c.id)
      setClienteNombre(c.empresa)
      setClienteRuc(c.ruc_dni ?? '')
      // Contacto por defecto: el de FACTURACIÓN (fallback principal); editable abajo.
      const lista = c.contactos ?? []
      setClienteContactos(lista)
      const fact = contactoFacturacionDe(c)
      setContactoSelId(fact?.id ?? null)
      setClienteContacto(fact ? [fact.nombre, fact.apellido].filter(Boolean).join(' ') : '')
      setClienteEmail(fact?.email ?? '')
      // Auto-detect SUNAT document type
      if (c.es_extranjero) {
        setTipoDocSunat('02')
      } else if (c.tipo_documento_fiscal === 'DNI') {
        setTipoDocSunat('03')
      } else {
        setTipoDocSunat('01')
      }
      // Reset project when changing client
      setProyectoNombre('')
      setProyectoId(null)
    }
  }

  function selectProyecto(pid: string) {
    if (pid === '__custom__') {
      setProyectoId(null)
      // Keep current text so user can type
      return
    }
    if (pid === '') {
      setProyectoId(null)
      setProyectoNombre('')
      return
    }
    const p = proyectosCliente.find(x => x.id === Number(pid))
    if (p) {
      setProyectoId(p.id)
      setProyectoNombre(p.nombre)
    }
  }

  // ─── Items management ───
  function updateItem(idx: number, field: keyof FacturaItem, value: string | number) {
    setItems(prev => prev.map((item, i) => {
      if (i !== idx) return item
      const updated = { ...item, [field]: value }
      if (field === 'precio_unitario' || field === 'cantidad') {
        updated.total = r2(updated.precio_unitario * updated.cantidad)
      }
      return updated
    }))
  }

  function addItem() {
    setItems(prev => [...prev, { ...emptyItem }])
  }

  function addFromCatalog(s: Servicio) {
    setItems(prev => [...prev, {
      tipo: 'fijo' as const,
      descripcion: s.nombre,
      precio_unitario: s.precio_base,
      cantidad: 1,
      total: s.precio_base,
    }])
  }

  function removeItem(idx: number) {
    setItems(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev)
  }

  // ─── File upload ───
  async function handleUpload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg']
    if (!allowed.includes(file.type)) {
      showToast('Solo se permiten PDF, PNG o JPG', 'error')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('Archivo máximo 5 MB', 'error')
      return
    }

    setUploading(true)
    try {
      const storageId = facturaIdForStorage ?? Date.now()
      const path = `${storageId}/${file.name}`
      const { error } = await supabase.storage.from('facturas-adjuntos').upload(path, file, { upsert: true })
      if (error) throw error

      const { data: urlData } = supabase.storage.from('facturas-adjuntos').getPublicUrl(path)

      setAdjuntos(prev => [...prev, {
        nombre: file.name,
        url: urlData.publicUrl,
        tipo: file.type,
        subido_at: new Date().toISOString(),
      }])
      showToast('Archivo adjuntado', 'success')
    } catch {
      showToast('Error al subir archivo', 'error')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  function removeAdjunto(idx: number) {
    setAdjuntos(prev => prev.filter((_, i) => i !== idx))
  }

  // ─── Save ───
  async function handleSave(targetEstado: 'borrador' | 'emitida') {
    if (!clienteNombre.trim()) { showToast('Selecciona un cliente', 'error'); return }
    if (items.every(i => !i.descripcion.trim())) { showToast('Agrega al menos un ítem', 'error'); return }
    if (moneda !== 'PEN' && (!tipoCambio || tipoCambio <= 0)) { showToast('Ingresa el tipo de cambio', 'error'); return }

    setSaving(true)
    try {
      const record: Record<string, unknown> = {
        numero,
        cliente_id: clienteId,
        cliente_nombre: clienteNombre,
        cliente_contacto: clienteContacto,
        cliente_ruc: clienteRuc,
        cliente_email: clienteEmail,
        proyecto_nombre: proyectoNombre,
        proyecto_id: proyectoId,  // FK real al proyecto (DAT-03)
        fecha_emision: fechaEmision || null,
        fecha_vencimiento: fechaVencimiento || null,
        items,
        adjuntos,
        subtotal,
        igv_porcentaje: igvPorcentaje,
        igv_monto: igvMonto,
        total,
        moneda,
        estado: targetEstado,
        notas: notas || null,
        nombre_interno: nombreInterno.trim() || null,
        tipo_cambio: moneda !== 'PEN' ? tipoCambio : null,
        detraccion_aplica: detraccion.aplica,
        detraccion_porcentaje: configSpot.porcentaje,
        detraccion_monto_pen: detraccion.montoPen,
        total_pen: detraccion.totalPen,
        tipo_documento_sunat: tipoDocSunat || null,
      }

      if (isEdit) {
        await db.update('facturas', { id: Number(id), ...record })
        showToast(targetEstado === 'borrador' ? 'Borrador guardado' : 'Factura emitida', 'success')
      } else {
        await db.add('facturas', record)
        showToast(targetEstado === 'borrador' ? 'Borrador creado — descarga el PDF para revisión' : 'Factura creada y emitida', 'success')
      }
      nav('/facturas')
    } catch {
      showToast('Error al guardar', 'error')
    } finally {
      setSaving(false)
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    // Default form submit saves as borrador
    handleSave('borrador')
  }

  return (
    <div className="page-enter">
      <PageHeader breadcrumbs={[{ label: 'Facturas', to: '/facturas' }, { label: isEdit ? 'Editar' : 'Nueva' }]} />

      <form onSubmit={handleSubmit}>
        {/* ─── Header section ─── */}
        <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>Datos Generales</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                N° Factura
              </label>
              {SUNAT_API_ENABLED ? (
                <input className="form-input" value={numero} readOnly style={{ fontFamily: 'monospace', fontWeight: 700 }} />
              ) : (
                <input
                  className="form-input"
                  value={numero}
                  onChange={e => setNumero(e.target.value)}
                  placeholder="E001-00000000"
                  style={{ fontFamily: 'monospace', fontWeight: 700 }}
                />
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Nombre Interno</label>
              <input
                className="form-input"
                value={nombreInterno}
                onChange={e => setNombreInterno(e.target.value)}
                placeholder="Ej: Abril 2026, Sprint 3, Hosting Q2..."
              />
            </div>
            <div className="form-group">
              <label className="form-label">Cliente *</label>
              <select
                className="form-select"
                value={clienteId ?? ''}
                onChange={e => selectCliente(Number(e.target.value))}
              >
                <option value="">Seleccionar cliente...</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.empresa}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Contacto de la empresa (elegido desde la relación) */}
          {clienteId && (
            <div className="form-group">
              <label className="form-label">Contacto de facturación</label>
              {clienteContactos.length > 0 ? (
                <select
                  className="form-select"
                  value={contactoSelId ?? ''}
                  onChange={e => elegirContacto(e.target.value ? Number(e.target.value) : null)}
                >
                  {clienteContactos.map(ct => {
                    const roles = ct.roles?.length ? ct.roles.join(', ') : (ct.es_principal ? 'PRINCIPAL' : '')
                    const nombre = [ct.nombre, ct.apellido].filter(Boolean).join(' ')
                    return (
                      <option key={ct.id} value={ct.id}>
                        {nombre}{roles ? ` — ${roles}` : ''}{ct.email ? ` · ${ct.email}` : ''}
                      </option>
                    )
                  })}
                </select>
              ) : (
                <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  Esta empresa no tiene contactos. Agrégalos en Contactos para poder elegir uno.
                </p>
              )}
            </div>
          )}

          {clienteNombre && (
            <div style={{ background: 'var(--bg-tertiary)', padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1rem', fontSize: '0.85rem' }}>
              <strong>{clienteNombre}</strong>
              {clienteRuc && <span style={{ marginLeft: 12, color: 'var(--text-secondary)' }}>RUC: {clienteRuc}</span>}
              {clienteContacto && <span style={{ marginLeft: 12, color: 'var(--text-secondary)' }}>{clienteContacto}</span>}
              {clienteEmail && <span style={{ marginLeft: 12, color: 'var(--text-secondary)' }}>{clienteEmail}</span>}
            </div>
          )}

          {/* Tipo Documento SUNAT */}
          {SUNAT_API_ENABLED && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Tipo Comprobante SUNAT</label>
                <select
                  className="form-select"
                  value={tipoDocSunat}
                  onChange={e => setTipoDocSunat(e.target.value as TipoDocumentoSunat)}
                >
                  <option value="">Auto-detectar</option>
                  <option value="01">Factura Electronica</option>
                  <option value="03">Boleta de Venta</option>
                  <option value="02">Factura Exportacion</option>
                </select>
                {tipoDocSunat && (
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>
                    {tipoDocSunat === '01' ? 'Serie F001 — Cliente con RUC' : tipoDocSunat === '03' ? 'Serie B001 — Cliente con DNI' : 'Serie FE01 — Cliente extranjero, IGV 0%'}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Proyecto</label>
              {clienteId && proyectosCliente.length > 0 ? (
                <>
                  <select
                    className="form-select"
                    value={proyectoId ?? (proyectoNombre && !proyectoId ? '__custom__' : '')}
                    onChange={e => selectProyecto(e.target.value)}
                  >
                    <option value="">Sin proyecto</option>
                    {proyectosCliente.map(p => (
                      <option key={p.id} value={p.id}>{p.nombre}</option>
                    ))}
                    <option value="__custom__">Otro (escribir manualmente)...</option>
                  </select>
                  {!proyectoId && proyectoNombre !== '' && (
                    <input
                      className="form-input"
                      value={proyectoNombre}
                      onChange={e => setProyectoNombre(e.target.value)}
                      placeholder="Nombre del proyecto"
                      style={{ marginTop: 6 }}
                    />
                  )}
                </>
              ) : (
                <input
                  className="form-input"
                  value={proyectoNombre}
                  onChange={e => setProyectoNombre(e.target.value)}
                  placeholder={clienteId ? 'Sin proyectos — escribir manualmente' : 'Selecciona un cliente primero'}
                  disabled={!clienteId}
                />
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Moneda</label>
              <select className="form-select" value={moneda} onChange={e => { setMoneda(e.target.value as Moneda); setTcAutoLoaded(false) }}>
                <option value="USD">USD</option>
                <option value="PEN">PEN</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Fecha Emisión</label>
              <input className="form-input" type="date" value={fechaEmision} onChange={e => { setFechaEmision(e.target.value); setTcAutoLoaded(false) }} />
            </div>
            <div className="form-group">
              <label className="form-label">Fecha Vencimiento</label>
              <input className="form-input" type="date" value={fechaVencimiento} onChange={e => setFechaVencimiento(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">IGV</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <select
                  className="form-input"
                  value={igvMode}
                  onChange={e => setIgvMode(e.target.value as 'agregar' | 'incluido')}
                  style={{ width: 'auto', minWidth: 140 }}
                >
                  <option value="agregar">+ Agregar IGV</option>
                  <option value="incluido">IGV incluido</option>
                </select>
                <input className="form-input" type="number" step="0.01" min="0" max="100" value={igvPorcentaje} onChange={e => setIgvPorcentaje(parseFloat(e.target.value) || 0)} style={{ width: 80 }} />
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>%</span>
              </div>
            </div>
          </div>

          {/* Tipo de cambio — solo si moneda ≠ PEN */}
          {moneda !== 'PEN' && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Tipo de Cambio Venta (SBS) — {moneda} → PEN
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    className="form-input"
                    type="number"
                    step="0.001"
                    min="0"
                    value={tipoCambio ?? ''}
                    onChange={e => setTipoCambio(e.target.value ? parseFloat(e.target.value) : null)}
                    placeholder="Ej: 3.800"
                    style={{ maxWidth: 200 }}
                  />
                  {tcAutoLoaded && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--accent-primary)', background: 'color-mix(in srgb, var(--rol-principal) 10%, transparent)', padding: '2px 8px', borderRadius: 4 }}>
                      ✓ Auto (SUNAT)
                    </span>
                  )}
                </div>
                {tipoCambio && total > 0 && (
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>
                    Total en PEN: S/ {r2(total * tipoCambio).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ─── Items table ─── */}
        <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ color: 'var(--text-primary)' }}>Ítems</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              {servicios.length > 0 && (
                <div style={{ position: 'relative' }}>
                  <select
                    className="form-select"
                    style={{ fontSize: '0.8rem' }}
                    value=""
                    onChange={e => {
                      const s = servicios.find(x => x.id === Number(e.target.value))
                      if (s) addFromCatalog(s)
                    }}
                  >
                    <option value="">+ Desde catálogo...</option>
                    {Array.from(new Set(servicios.map(s => s.categoria || 'Sin categoría'))).map(cat => (
                      <optgroup key={cat} label={cat}>
                        {servicios.filter(s => (s.categoria || 'Sin categoría') === cat).map(s => (
                          <option key={s.id} value={s.id}>{s.nombre} — {formatCurrency(s.precio_base, s.moneda)}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
              )}
              <button type="button" className="btn btn-secondary btn-sm" onClick={addItem}>+ Ítem manual</button>
            </div>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 100 }}>Tipo</th>
                  <th>Descripción</th>
                  <th style={{ width: 130 }}>Precio Unit.</th>
                  <th style={{ width: 80 }}>Cant.</th>
                  <th style={{ width: 130 }}>Total</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx}>
                    <td>
                      <select
                        className="form-select"
                        value={item.tipo}
                        onChange={e => updateItem(idx, 'tipo', e.target.value)}
                        style={{ fontSize: '0.8rem' }}
                      >
                        <option value="fijo">Fijo</option>
                        <option value="variable">Variable</option>
                      </select>
                    </td>
                    <td>
                      <input
                        className="form-input"
                        value={item.descripcion}
                        onChange={e => updateItem(idx, 'descripcion', e.target.value)}
                        placeholder={item.tipo === 'fijo' ? 'Administración de Infraestructura Cloud...' : 'Excedente de Consumo RAM/CPU...'}
                      />
                    </td>
                    <td>
                      <input
                        className="form-input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.precio_unitario}
                        onChange={e => updateItem(idx, 'precio_unitario', parseFloat(e.target.value) || 0)}
                      />
                    </td>
                    <td>
                      <input
                        className="form-input"
                        type="number"
                        min="1"
                        value={item.cantidad}
                        onChange={e => updateItem(idx, 'cantidad', parseInt(e.target.value) || 1)}
                      />
                    </td>
                    <td style={{ fontWeight: 700, textAlign: 'right' }}>
                      {formatCurrency(item.total, moneda)}
                    </td>
                    <td>
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => removeItem(idx)} title="Eliminar ítem">×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <div style={{ minWidth: 320 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-primary)' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Subtotal</span>
                <span style={{ fontWeight: 600 }}>{formatCurrency(subtotal, moneda)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-primary)' }}>
                <span style={{ color: 'var(--text-secondary)' }}>IGV ({igvPorcentaje}%)</span>
                <span style={{ fontWeight: 600 }}>{formatCurrency(igvMonto, moneda)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontWeight: 700, fontSize: '1.1rem' }}>
                <span>Total</span>
                <span style={{ color: 'var(--accent-primary)' }}>{formatCurrency(total, moneda)}</span>
              </div>

              {/* Detracción SPOT */}
              {detraccion.aplica && (
                <div style={{
                  marginTop: 12, padding: '12px 14px', borderRadius: 8,
                  background: 'color-mix(in srgb, var(--danger) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
                }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 6, color: 'var(--danger)' }}>
                    DETRACCIÓN SPOT APLICA
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
                    Código {configSpot.codigo} — {configSpot.descripcion}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span>Total PEN</span>
                    <span>S/ {detraccion.totalPen.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span>Detracción ({configSpot.porcentaje}%)</span>
                    <span style={{ fontWeight: 700 }}>S/ {detraccion.montoPen.toLocaleString('es-PE')}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', paddingTop: 4, borderTop: '1px solid color-mix(in srgb, var(--danger) 20%, transparent)' }}>
                    <span>Neto a recibir</span>
                    <span style={{ fontWeight: 700 }}>S/ {(detraccion.totalPen - detraccion.montoPen).toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 6 }}>
                    Cta. BN: {configSpot.cuenta_bn}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ─── Adjuntos ─── */}
        <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>
            {SUNAT_API_ENABLED ? 'Sustento de Consumo Cloud' : 'Comprobante SUNAT (PDF)'}
          </h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            {SUNAT_API_ENABLED
              ? 'Adjunta screenshots o reportes de los paneles de consumo (Supabase, Railway, etc.) para sustentar los cobros variables.'
              : 'Adjunta el PDF del comprobante descargado desde el Portal SOL de SUNAT como soporte contable de este cobro.'}
          </p>

          <div style={{ marginBottom: '1rem' }}>
            <label className="btn btn-secondary" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {uploading ? (
                <><div className="auth-spinner" style={{ width: 16, height: 16 }} /> Subiendo...</>
              ) : (
                <>Subir archivo (PDF, PNG, JPG — max 5MB)</>
              )}
              <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleUpload} style={{ display: 'none' }} disabled={uploading} />
            </label>
          </div>

          {adjuntos.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {adjuntos.map((adj, idx) => (
                <div key={idx} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '0.5rem 0.75rem', background: 'var(--bg-tertiary)', borderRadius: 8,
                }}>
                  <span style={{ fontSize: '1.2rem' }}>
                    {adj.tipo.includes('pdf') ? 'PDF' : 'IMG'}
                  </span>
                  <span style={{ flex: 1, fontSize: '0.85rem' }}>{adj.nombre}</span>
                  <a href={adj.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary">Ver</a>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => removeAdjunto(idx)}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ─── Notes ─── */}
        <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>Notas</h3>
          <textarea
            className="form-textarea"
            value={notas}
            onChange={e => setNotas(e.target.value)}
            rows={3}
            placeholder="Notas o condiciones adicionales..."
          />
        </div>

        {/* ─── Actions ─── */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
          <button type="button" className="btn btn-secondary" onClick={() => nav('/facturas')}>Cancelar</button>
          <button
            type="submit"
            className="btn"
            disabled={saving}
            style={{
              background: 'var(--veil-08)',
              border: '1px solid var(--border-primary)',
              color: 'var(--text-primary)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
            title="Guarda como borrador para revisión y descarga de PDF antes de emitir"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            {saving ? 'Guardando...' : 'Guardar Borrador'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving}
            onClick={() => handleSave('emitida')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            title="Marca la factura como Emitida (lista para cobro). NO la emite ante SUNAT — eso se hace desde el detalle de la factura."
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/>
            </svg>
            {saving ? 'Guardando...' : isEdit ? 'Actualizar y marcar Emitida' : 'Crear y marcar Emitida'}
          </button>
        </div>
      </form>
    </div>
  )
}
